// =====================================================================
// ProtocolList (EHR Protocolos / S63) — aba "Protocolos" do prontuário.
// Cartão por pacote: progresso + checklist das doses. Aplicar dose registra
// foto/assinatura e baixa o estoque. Pacote com valor gera contas a receber.
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Button, Typography, Box, Chip, LinearProgress, CircularProgress,
  Divider, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import GestureIcon from "@material-ui/icons/Gesture";
import { toast } from "react-toastify";
import {
  listProtocols, cancelProtocol, getDosePhotoUrl, getDoseSignatureUrl,
  PROTOCOL_STATUS, protocolErrorMessage
} from "../../services/protocolService";
import { fmtMoney } from "../../services/financeService";
import ProtocolFormDialog from "./ProtocolFormDialog";
import DoseApplyDialog from "./DoseApplyDialog";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  wrap: { padding: theme.spacing(1) },
  card: { padding: theme.spacing(2), marginBottom: theme.spacing(2), border: `1px solid ${theme.palette.divider}` },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  doseRow: { display: "flex", alignItems: "center", gap: 8, padding: theme.spacing(0.5, 0) },
  empty: { padding: theme.spacing(6), textAlign: "center", color: theme.palette.text.secondary }
}));

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

// Miniatura da foto de uma dose (evolução) — busca o blob auth-gated lazy.
const DoseThumb = ({ doseId }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let made = null;
    getDosePhotoUrl(doseId)
      .then((u) => { if (alive) { made = u; setUrl(u); } else { URL.revokeObjectURL(u); } })
      .catch(() => {});
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [doseId]);
  if (!url) return null;
  return (
    <img src={url} alt="dose" title="Ver foto" onClick={() => window.open(url, "_blank")}
      style={{ height: 44, width: 44, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd", cursor: "pointer" }} />
  );
};

const ProtocolList = ({ patientId }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [applyTarget, setApplyTarget] = useState(null); // { protocol, dose }
  const [cancelTarget, setCancelTarget] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProtocols(patientId);
      setProtocols(Array.isArray(data?.protocols) ? data.protocols : []);
    } catch (e) {
      toast.error("Falha ao carregar protocolos.");
      setProtocols([]);
    } finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { fetch(); }, [fetch]);

  const openFile = async (loader, doseId) => {
    try {
      const url = await loader(doseId);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Não foi possível abrir o arquivo."); }
  };

  const handleCancel = async () => {
    const p = cancelTarget;
    setCancelTarget(null);
    try {
      await cancelProtocol(patientId, p.id);
      toast.success("Protocolo cancelado.");
      fetch();
    } catch (err) { toast.error(protocolErrorMessage(err)); }
  };

  return (
    <div className={classes.wrap}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
          Novo Protocolo
        </Button>
      </Box>

      {loading ? (
        <div className={classes.empty}><CircularProgress /></div>
      ) : protocols.length === 0 ? (
        <div className={classes.empty}>
          <Typography>Nenhum protocolo. Clique em "Novo Protocolo" para criar um pacote de doses.</Typography>
        </div>
      ) : (
        protocols.map((p) => {
          const done = Number(p.dosesDone) || 0;
          const total = Number(p.totalDoses) || 0;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          const st = PROTOCOL_STATUS[p.status] || { label: p.status, color: "#757575" };
          const firstPendingId = ((p.doses || []).find((x) => x.status === "pendente") || {}).id;
          return (
            <Paper key={p.id} variant="outlined" className={classes.card}>
              <div className={classes.head}>
                <div>
                  <Typography variant="h6">{p.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {p.productName ? `${p.productName} · ` : ""}{done}/{total} doses
                    {Number(p.price) > 0 ? ` · ${fmtMoney(p.price)}` : ""}
                  </Typography>
                </div>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  {Number(p.price) > 0 && p.quoteId && (
                    <Chip size="small" label="💰 no financeiro" variant="outlined" />
                  )}
                  <Chip size="small" label={st.label} style={{ backgroundColor: st.color, color: "#fff", fontWeight: 600 }} />
                  {p.status === "ativo" && (
                    <Button size="small" onClick={() => setCancelTarget(p)}>Cancelar</Button>
                  )}
                </Box>
              </div>

              <Box my={1.5}><LinearProgress variant="determinate" value={pct} /></Box>

              <Divider />
              <Box mt={1}>
                {(p.doses || []).map((d) => {
                  const applied = d.status === "aplicada";
                  const isNext = d.id === firstPendingId;
                  return (
                    <div key={d.id} className={classes.doseRow}>
                      {applied
                        ? <CheckCircleIcon fontSize="small" style={{ color: "#2e7d32" }} />
                        : <RadioButtonUncheckedIcon fontSize="small" style={{ color: "#9e9e9e" }} />}
                      <Typography variant="body2" style={{ minWidth: 70 }}>Dose {d.doseNumber}</Typography>
                      {applied ? (
                        <>
                          <Typography variant="body2" color="textSecondary" style={{ flex: 1 }}>
                            {fmtDate(d.appliedAt)}{d.notes ? ` — ${d.notes}` : ""}
                          </Typography>
                          {d.hasPhoto && <DoseThumb doseId={d.id} />}
                          {d.hasSignature && (
                            <Tooltip title="Ver assinatura"><IconButton size="small" onClick={() => openFile(getDoseSignatureUrl, d.id)}><GestureIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                        </>
                      ) : (
                        <>
                          {isNext && <Chip size="small" label="próxima" style={{ backgroundColor: "#1565c0", color: "#fff", height: 20 }} />}
                          <Box flexGrow={1} />
                          {p.status === "ativo" && (
                            <Button size="small" color="primary" variant={isNext ? "contained" : "outlined"}
                              onClick={() => setApplyTarget({ protocol: p, dose: d })}>
                              Aplicar
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </Box>
            </Paper>
          );
        })
      )}

      <ProtocolFormDialog open={formOpen} patientId={patientId}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); fetch(); }} />

      <DoseApplyDialog open={!!applyTarget} patientId={patientId}
        protocol={applyTarget?.protocol} dose={applyTarget?.dose}
        onClose={() => setApplyTarget(null)}
        onApplied={() => { setApplyTarget(null); fetch(); }} />

      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Cancelar protocolo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cancelar "{cancelTarget?.name}"? As doses já aplicadas e a baixa de estoque permanecem no histórico.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>Voltar</Button>
          <Button onClick={handleCancel} style={{ color: "#c62828" }}>Cancelar protocolo</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProtocolList;
